import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const TypewriterCycle = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [displayedText, setDisplayedText] = useState('');
    const [isTyping, setIsTyping] = useState(true);
    const [showCursor, setShowCursor] = useState(true);

    const titles = [
        {
            line1: "Echo Media",
            line2: "Your Digital Innovation Partner",
            highlight: false
        },
        {
            line1: "Crafting Echoes",
            line2: "in the Digital Universe",
            highlight: true,
            highlightText: "Digital Universe"
        },
        {
            line1: "Transform your idea",
            line2: "into a digital solution",
            highlight: false
        }
    ];

    const currentTitle = titles[currentIndex];
    const fullText = `${currentTitle.line1}\n${currentTitle.line2}`;

    useEffect(() => {
        let typingTimeout;
        let charIndex = 0;

        const typeNextChar = () => {
            if (charIndex <= fullText.length) {
                setDisplayedText(fullText.slice(0, charIndex));
                charIndex++;
                typingTimeout = setTimeout(typeNextChar, 50); // Typing speed
            } else {
                setIsTyping(false);
                // Wait 3 seconds then move to next title
                typingTimeout = setTimeout(() => {
                    setDisplayedText('');
                    setIsTyping(true);
                    setCurrentIndex((prev) => (prev + 1) % titles.length);
                }, 3000);
            }
        };

        typeNextChar();

        return () => clearTimeout(typingTimeout);
    }, [currentIndex, fullText, titles.length]);

    // Blinking cursor effect
    useEffect(() => {
        const cursorInterval = setInterval(() => {
            setShowCursor((prev) => !prev);
        }, 500);
        return () => clearInterval(cursorInterval);
    }, []);

    const lines = displayedText.split('\n');
    const line1 = lines[0] || '';
    const line2 = lines[1] || '';

    return (
        <div className="min-h-[200px] md:min-h-[250px]">
            <AnimatePresence mode="wait">
                <motion.div
                    key={currentIndex}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    {/* Line 1 */}
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white font-mono leading-tight mb-2">
                        {line1}
                        {line2 === '' && (
                            <span className={`text-cyan-400 ${showCursor ? 'opacity-100' : 'opacity-0'}`}>|</span>
                        )}
                    </h1>

                    {/* Line 2 */}
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-mono leading-tight">
                        {currentTitle.highlight && currentTitle.highlightText ? (
                            <>
                                {line2.includes(currentTitle.highlightText) ? (
                                    <>
                                        <span className="text-white">
                                            {line2.slice(0, line2.indexOf(currentTitle.highlightText))}
                                        </span>
                                        <span
                                            className="text-cyan-400"
                                            style={{
                                                textShadow: '0 0 30px rgba(34, 211, 238, 0.6), 0 0 60px rgba(34, 211, 238, 0.3)'
                                            }}
                                        >
                                            {line2.slice(line2.indexOf(currentTitle.highlightText))}
                                        </span>
                                    </>
                                ) : (
                                    <span className="text-white">{line2}</span>
                                )}
                            </>
                        ) : (
                            <span className="text-slate-300">{line2}</span>
                        )}
                        {line2 !== '' && (
                            <span className={`text-cyan-400 ${showCursor ? 'opacity-100' : 'opacity-0'}`}>|</span>
                        )}
                    </h2>
                </motion.div>
            </AnimatePresence>

            {/* Progress dots */}
            <div className="flex gap-2 mt-8">
                {titles.map((_, index) => (
                    <motion.div
                        key={index}
                        className={`h-1 rounded-full transition-all duration-300 ${index === currentIndex ? 'w-8 bg-cyan-400' : 'w-2 bg-slate-600'
                            }`}
                    />
                ))}
            </div>
        </div>
    );
};

export default TypewriterCycle;
